import type { ApiPlayable } from "@hive/sdk";
import { playableLevel } from "./catalogueLevel";

export type CatalogueSize = "small" | "large";
export interface CatalogueTile<T> { item: T; sourceIndex: number; size: CatalogueSize }

export const MOSAIC_CANDIDATES = 24;
const MAX_MOSAIC_COLUMNS = 32;
export interface MosaicTile<T> extends CatalogueTile<T> { column: number; row: number; span: number }

export function mosaicColumns(width: number) {
	return Math.max(3, Math.min(MAX_MOSAIC_COLUMNS, Math.floor(width / 132)));
}

/** Grades select a fixed size; artwork and the surrounding games never demote SP. */
export function catalogueSpan(item: ApiPlayable): 1 | 2 {
	return playableLevel(item) === "SP" ? 2 : 1;
}

function variation(seed: string) {
	return Array.from(seed).reduce((hash, char) => (Math.imul(hash, 31) + char.charCodeAt(0)) >>> 0, 0) % 12;
}

const placementCache = new Map<string, number[][]>();
/** Non-overlapping two-column positions, in a stable order that varies across themes. */
function largePositions(columns: number, count: number, variant: number): number[] {
	if (columns > 12) {
		// Sample spaced positions. Enumerating every combination grows
		// exponentially on wide screens; keep the established desktop patterns below.
		const positions = Array.from({ length: columns - count }, (_, index) => index);
		let random = (0x9e3779b9 ^ Math.imul(variant + 1, 0x85ebca6b) ^ (columns << 8) ^ count) >>> 0;
		for (let index = positions.length - 1; index > 0; index--) {
			random ^= random << 13;
			random ^= random >>> 17;
			random ^= random << 5;
			const swap = (random >>> 0) % (index + 1);
			[positions[index], positions[swap]] = [positions[swap]!, positions[index]!];
		}
		// Reinsert the second column occupied by each preceding SP card.
		return positions.slice(0, count).sort((a, b) => a - b).map((column, index) => column + index);
	}
	const key = `${columns}:${count}`;
	let choices = placementCache.get(key);
	if (!choices) {
		choices = [];
		const visit = (positions: number[], next: number) => {
			if (positions.length === count) { choices!.push(positions); return; }
			const left = count - positions.length;
			for (let column = next; column <= columns - left * 2; column++) visit([...positions, column], column + 2);
		};
		visit([], 0);
		const score = (positions: number[]) => {
			let value = positions.reduce((hash, column) => Math.imul(hash ^ (column + 1), 16777619), 2166136261);
			value = Math.imul(value ^ (value >>> 16), 0x45d9f3b);
			return (value ^ (value >>> 16)) >>> 0;
		};
		choices.sort((a, b) => score(a) - score(b));
		placementCache.set(key, choices);
	}
	return choices[variant % choices.length]!;
}

/** Pack SP at 2×2 and every other grade at 1×1; only a genuinely short tail may be incomplete. */
export function catalogueComposition<T>(items: T[], playable: (item: T) => ApiPlayable, _cover: (item: T) => unknown,
	options: { columns?: number; seed?: string; compact?: boolean; pattern?: number; rows?: 2 } = {}) {
	const requestedColumns = Math.max(3, Math.min(MAX_MOSAIC_COLUMNS, options.columns ?? 9));
	const candidates = items.slice(0, Math.max(MOSAIC_CANDIDATES, requestedColumns * 2))
		.map((item, sourceIndex) => ({ item, sourceIndex, span: catalogueSpan(playable(item)) }));
	const large = candidates.filter((entry) => entry.span === 2);
	const small = candidates.filter((entry) => entry.span === 1);
	const rows = options.rows ?? (large.length ? 2 : 1);
	const variant = options.pattern === undefined ? variation(options.seed ?? "") : Math.abs(options.pattern) % 12;
	let plan: { columns: number; largeCount: number; smallCount: number } | undefined;
	// An all-SP band needs an even column count. Change at most one column, never a game's tier.
	for (const columns of rows === 2 ? [requestedColumns, requestedColumns - 1] : [requestedColumns]) {
		for (let largeCount = Math.min(large.length, Math.floor(columns / 2)); largeCount >= (large.length ? 1 : 0); largeCount--) {
			const smallCount = rows * columns - largeCount * 4;
			if (smallCount >= 0 && smallCount <= small.length) { plan = { columns, largeCount, smallCount }; break; }
		}
		if (plan) break;
	}
	if (!plan) {
		const largeCount = Math.min(large.length, Math.floor(requestedColumns / 2));
		plan = { columns: requestedColumns, largeCount, smallCount: Math.min(small.length, requestedColumns * rows - largeCount * 4) };
	}
	const { columns, largeCount, smallCount } = plan;
	const complete = largeCount * 4 + smallCount === rows * columns;
	const occupied = Array.from({ length: rows }, () => Array(columns).fill(false));
	const tiles: MosaicTile<T>[] = [];
	function place(entry: typeof candidates[number], column: number, row: number) {
		for (let y = row; y < row + entry.span; y++) for (let x = column; x < column + entry.span; x++) occupied[y][x] = true;
		tiles.push({ ...entry, column, row, size: entry.span === 2 ? "large" : "small" });
	}
	const positions = complete ? largePositions(columns, largeCount, variant) : Array.from({ length: largeCount }, (_, index) => index * 2);
	large.slice(0, largeCount).forEach((entry, index) => place(entry, positions[index]!, 0));
	let index = 0;
	for (let column = 0; column < columns; column++) for (let row = 0; row < rows; row++) {
		if (!occupied[row][column] && index < smallCount) place(small[index++]!, column, row);
	}
	const selected = new Set(tiles.map((tile) => tile.sourceIndex));
	return { tiles, columns, rows, tiers: rows, variant,
		rest: items.map((item, sourceIndex) => ({ item, sourceIndex, size: catalogueSpan(playable(item)) === 2 ? "large" as const : "small" as const }))
			.filter((entry) => !selected.has(entry.sourceIndex)) };
}
